import { MAX_SCREEN_IMAGE_EDGE_PX } from '@/config/mockupScreenMediaLimits'

export type ScreenImageDimensionResult =
  | { ok: true; width: number; height: number }
  | { ok: false; reason: 'gif_not_supported' | 'load_failed' | 'oversize'; width?: number; height?: number }

export type PreparedScreenRasterFileResult =
  | {
      ok: true
      file: File
      width: number
      height: number
      originalWidth: number
      originalHeight: number
      resized: boolean
    }
  | { ok: false; reason: 'gif_not_supported' | 'load_failed' }

/**
 * 首版拒绝 GIF（避免动图多帧语义歧义）；与 `accept` 白名单配合使用。
 */
export function isRejectedAnimatedRasterScreenFile(file: File): boolean {
  const t = (file.type || '').toLowerCase()
  if (t === 'image/gif') return true
  return /\.gif$/i.test(file.name)
}

/**
 * 读取本地栅格图片的自然宽高（浏览器解码）。
 *
 * @param file - 本地文件
 */
export function measureImageFileDimensions(file: File): Promise<ScreenImageDimensionResult> {
  if (isRejectedAnimatedRasterScreenFile(file)) {
    return Promise.resolve({ ok: false, reason: 'gif_not_supported' })
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      const w = img.naturalWidth
      const h = img.naturalHeight
      if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1) {
        resolve({ ok: false, reason: 'load_failed' })
        return
      }
      if (w > MAX_SCREEN_IMAGE_EDGE_PX || h > MAX_SCREEN_IMAGE_EDGE_PX) {
        resolve({ ok: false, reason: 'oversize', width: w, height: h })
        return
      }
      resolve({ ok: true, width: w, height: h })
    }
    img.onerror = () => {
      try {
        URL.revokeObjectURL(url)
      } catch {
        /* ignore */
      }
      resolve({ ok: false, reason: 'load_failed' })
    }
    img.src = url
  })
}

export function fitImageDimensionsWithinLimit(
  width: number,
  height: number,
  maxEdgePx: number = MAX_SCREEN_IMAGE_EDGE_PX,
): { width: number; height: number; resized: boolean } {
  const w = Math.max(1, Math.round(width))
  const h = Math.max(1, Math.round(height))
  if (w <= maxEdgePx && h <= maxEdgePx) {
    return { width: w, height: h, resized: false }
  }
  const scale = Math.min(maxEdgePx / w, maxEdgePx / h)
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
    resized: true,
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality)
  })
}

function preferredRasterOutputMime(file: File): string {
  const t = (file.type || '').toLowerCase()
  if (t === 'image/png' || t === 'image/jpeg' || t === 'image/webp') return t
  return 'image/png'
}

function drawDownscaledImage(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number,
): HTMLCanvasElement | null {
  let currentSource: CanvasImageSource = source
  let currentWidth = sourceWidth
  let currentHeight = sourceHeight

  while (currentWidth / 2 >= targetWidth * 1.25 || currentHeight / 2 >= targetHeight * 1.25) {
    const nextWidth = Math.max(targetWidth, Math.round(currentWidth / 2))
    const nextHeight = Math.max(targetHeight, Math.round(currentHeight / 2))
    const stepCanvas = document.createElement('canvas')
    stepCanvas.width = nextWidth
    stepCanvas.height = nextHeight
    const stepCtx = stepCanvas.getContext('2d')
    if (!stepCtx) return null
    stepCtx.imageSmoothingEnabled = true
    stepCtx.imageSmoothingQuality = 'high'
    stepCtx.clearRect(0, 0, nextWidth, nextHeight)
    stepCtx.drawImage(currentSource, 0, 0, currentWidth, currentHeight, 0, 0, nextWidth, nextHeight)
    currentSource = stepCanvas
    currentWidth = nextWidth
    currentHeight = nextHeight
  }

  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.clearRect(0, 0, targetWidth, targetHeight)
  ctx.drawImage(currentSource, 0, 0, currentWidth, currentHeight, 0, 0, targetWidth, targetHeight)
  return canvas
}

export async function prepareScreenRasterFileForIngest(file: File): Promise<PreparedScreenRasterFileResult> {
  if (isRejectedAnimatedRasterScreenFile(file)) {
    return { ok: false, reason: 'gif_not_supported' }
  }

  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('load_failed'))
      el.src = url
    })

    const originalWidth = img.naturalWidth
    const originalHeight = img.naturalHeight
    if (!Number.isFinite(originalWidth) || !Number.isFinite(originalHeight) || originalWidth < 1 || originalHeight < 1) {
      return { ok: false, reason: 'load_failed' }
    }

    const next = fitImageDimensionsWithinLimit(originalWidth, originalHeight, MAX_SCREEN_IMAGE_EDGE_PX)
    if (!next.resized) {
      return {
        ok: true,
        file,
        width: originalWidth,
        height: originalHeight,
        originalWidth,
        originalHeight,
        resized: false,
      }
    }

    const canvas = drawDownscaledImage(img, originalWidth, originalHeight, next.width, next.height)
    if (!canvas) return { ok: false, reason: 'load_failed' }

    const mime = preferredRasterOutputMime(file)
    const blob = await canvasToBlob(canvas, mime, mime === 'image/jpeg' ? 0.92 : undefined)
    if (!blob) return { ok: false, reason: 'load_failed' }

    const resizedFile = new File([blob], file.name, {
      type: blob.type || mime,
      lastModified: file.lastModified,
    })
    return {
      ok: true,
      file: resizedFile,
      width: next.width,
      height: next.height,
      originalWidth,
      originalHeight,
      resized: true,
    }
  } catch {
    return { ok: false, reason: 'load_failed' }
  } finally {
    try {
      URL.revokeObjectURL(url)
    } catch {
      /* ignore */
    }
  }
}
